import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Download } from "lucide-react";
import { exportToCsv } from "@/lib/exportCsv";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ServiceRequestsDetailDialog = ({ open, onOpenChange }: Props) => {
  const { data: requests = [], isLoading } = useQuery({
    queryKey: ["service-requests-detail"],
    queryFn: async () => {
      const { data: reqs } = await supabase
        .from("service_requests")
        .select("id, subject, category, status, created_at, student_id")
        .order("created_at", { ascending: false })
        .limit(100);

      if (!reqs?.length) return [];

      const studentIds = [...new Set(reqs.map((r) => r.student_id))];
      const { data: students } = await supabase.from("students").select("id, first_name, last_name, student_number").in("id", studentIds);
      const map = new Map((students ?? []).map((s) => [s.id, `${s.first_name} ${s.last_name} (${s.student_number})`]));

      return reqs.map((r) => ({ ...r, student_name: map.get(r.student_id) ?? "Unknown" }));
    },
    enabled: open,
  });

  const open_reqs = requests.filter((r) => r.status === "open");
  const escalated = requests.filter((r) => r.status === "escalated");
  const resolved = requests.filter((r) => r.status === "resolved");

  const handleExport = () => {
    const headers = ["Subject", "Student", "Category", "Status", "Date"];
    const rows = requests.map((r) => [r.subject, r.student_name, r.category ?? "", r.status, format(new Date(r.created_at), "yyyy-MM-dd")]);
    exportToCsv("service-requests.csv", headers, rows);
  };

  const renderList = (items: typeof requests) => (
    <div className="space-y-2 max-h-[400px] overflow-y-auto">
      {isLoading && <p className="text-sm text-muted-foreground text-center py-4">Loading…</p>}
      {!isLoading && items.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">No requests found</p>
      )}
      {items.map((r) => (
        <div key={r.id} className="flex items-start justify-between p-3 rounded-lg border bg-card">
          <div className="space-y-1 min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{r.subject}</p>
            <p className="text-xs text-muted-foreground truncate">{r.student_name}</p>
            <p className="text-xs text-muted-foreground">{r.category} · {format(new Date(r.created_at), "MMM d, yyyy")}</p>
          </div>
          <Badge variant={r.status === "escalated" ? "destructive" : r.status === "resolved" ? "secondary" : "default"} className="ml-2 shrink-0">
            {r.status}
          </Badge>
        </div>
      ))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="flex flex-row items-center justify-between gap-2">
          <DialogTitle>Service Requests</DialogTitle>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={requests.length === 0} className="mr-8 shrink-0">
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
        </DialogHeader>
        <Tabs defaultValue="open">
          <TabsList className="w-full">
            <TabsTrigger value="open" className="flex-1">Open ({open_reqs.length})</TabsTrigger>
            <TabsTrigger value="escalated" className="flex-1">Escalated ({escalated.length})</TabsTrigger>
            <TabsTrigger value="resolved" className="flex-1">Resolved ({resolved.length})</TabsTrigger>
          </TabsList>
          <TabsContent value="open">{renderList(open_reqs)}</TabsContent>
          <TabsContent value="escalated">{renderList(escalated)}</TabsContent>
          <TabsContent value="resolved">{renderList(resolved)}</TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
